import { InjectionMoldingOrderedProductStatus } from '../enums/injection-molding-ordered-product-status.enum';
import { InjectionMoldingOrderedProduct } from '../models/injection-molding-ordered-product.model';

/** Null means released work appears immediately; zero means on the due date. */
export function injectionMoldingFloorVisibilityDate(dueDate: Date, daysBeforeDue: number | null): Date | null {
    if (daysBeforeDue === null) return null;
    const floorDate = new Date(dueDate);
    floorDate.setHours(0, 0, 0, 0);
    floorDate.setDate(floorDate.getDate() - daysBeforeDue);
    return floorDate;
}

/** An office-facing date for work not yet visible to restricted production users. */
export function pendingInjectionMoldingFloorVisibilityDate(
    orderedProduct: Pick<InjectionMoldingOrderedProduct, 'dueDate' | 'status' | 'completedQuantity'>,
    daysBeforeDue: number | null,
    today = new Date(),
): Date | null {
    if (daysBeforeDue === null || orderedProduct.completedQuantity > 0 || ![
        InjectionMoldingOrderedProductStatus.Released,
        InjectionMoldingOrderedProductStatus.Hold,
    ].includes(orderedProduct.status)) return null;

    const floorDate = injectionMoldingFloorVisibilityDate(orderedProduct.dueDate, daysBeforeDue);
    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);
    return floorDate && floorDate.getTime() > todayStart.getTime() ? floorDate : null;
}

/**
 * Restrict unstarted work until its product's floor date. Work already started
 * cannot disappear if a due date or product revision later changes.
 */
export function canViewInjectionMoldingOrderedProductForFloorWindow(
    orderedProduct: Pick<InjectionMoldingOrderedProduct, 'dueDate' | 'status' | 'completedQuantity'>,
    daysBeforeDue: number | null,
    canViewFutureOrders: boolean,
    today = new Date(),
): boolean {
    if (daysBeforeDue === null || canViewFutureOrders || orderedProduct.completedQuantity > 0) return true;
    if ([
        InjectionMoldingOrderedProductStatus.ProductionComplete,
        InjectionMoldingOrderedProductStatus.ReadyToShip,
        InjectionMoldingOrderedProductStatus.Complete,
        InjectionMoldingOrderedProductStatus.Canceled,
    ].includes(orderedProduct.status)) return true;

    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);
    const floorDate = injectionMoldingFloorVisibilityDate(orderedProduct.dueDate, daysBeforeDue);
    return !!floorDate && floorDate.getTime() <= todayStart.getTime();
}
